import type BaseImageLayer from 'ol/layer/BaseImage';
import type ImageSource from 'ol/source/Image';
import type LayerRenderer from 'ol/renderer/Layer';
import type { Extent } from 'ol/extent';
import type { Projection as OLProjection } from 'ol/proj';

import { Projection } from '@/geo/utils/projection';
import { AbstractGVLayer } from '@/geo/layer/gv-layers/abstract-gv-layer';

/**
 * Abstract Geoview Layer managing an OpenLayer raster type layer.
 */
export abstract class AbstractGVRaster extends AbstractGVLayer {
  // #region OVERRIDES

  /**
   * Overrides the parent method to return a more specific OpenLayers layer type (covariant return).
   * @override
   * @returns {BaseImageLayer<ImageSource, LayerRenderer>} The strongly-typed OpenLayers type.
   */
  // Disabling 'any', because that's how it is in OpenLayers
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override getOLLayer(): BaseImageLayer<ImageSource, LayerRenderer<any>> {
    // Call parent and cast
    // Disabling 'any', because that's how it is in OpenLayers
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return super.getOLLayer() as BaseImageLayer<ImageSource, LayerRenderer<any>>;
  }

  // #endregion OVERRIDES

  // #region METHODS

  /**
   * Gets the metadata extent projection, if any.
   * @returns {OLProjection | undefined} The OpenLayer projection
   */
  getMetadataProjection(): OLProjection | undefined {
    // Get metadata
    // GV Can be any object so disable eslint and proceed with caution
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const metadata = this.getLayerConfig().getServiceMetadata() as any;

    // Redirect
    return Projection.getProjectionFromObj(metadata?.fullExtent?.spatialReference);
  }

  /**
   * Gets the metadata extent, if any.
   * @returns {Extent | undefined} The OpenLayer projection
   */
  getMetadataExtent(): Extent | undefined {
    // Get the layer metadata precisely
    // GV Can be any object so disable eslint and proceed with caution
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const extent = (this.getLayerConfig().getLayerMetadata() as any)?.extent;

    // If found
    if (extent) {
      return [extent.xmin, extent.ymin, extent.xmax, extent.ymax] as Extent;
    }

    // Here, we couldn't find the layer metadata, so we use the layer parent definition metadata
    // Get metadata
    // GV Can be any object so disable eslint and proceed with caution
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const metadata = this.getLayerConfig().getServiceMetadata() as any;
    if (metadata?.fullExtent) {
      return [metadata?.fullExtent.xmin, metadata?.fullExtent.ymin, metadata?.fullExtent.xmax, metadata?.fullExtent.ymax] as Extent;
    }

    // No layer metadata extent could be found
    return undefined;
  }

  // #endregion METHODS
}
